import { AspectRatio } from '@livecontrol/core-ui';
import { Time } from '@livecontrol/core-utils';
import { Event } from '@livecontrol/scheduler/model';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import type { ScheduledEvent } from '../../../graphql';
import { FallbackBackground } from '../fallback-background';
import * as States from './states';
import { Video } from './video';
import Phase = Event.Phase;

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  font-size: 20px;

  > .full {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &.z-3 {
      z-index: 3;
    }
  }

  &,
  > .full,
  > .background {
    border-radius: 8px;
    overflow: hidden;
  }

  .x-light-theme & {
    color: var(--dark);
  }
  .x-dark-theme & {
    color: var(--white);
  }
  video[poster] {
    object-fit: fill;
  }

  .x-viewport-md &,
  .x-viewport-lg &,
  .x-viewport-xl & {
    border-radius: 8px;
  }
`;

export const Player = ({
  scheduledEvent,
  ratio = 3 / 2
}: {
  scheduledEvent: ScheduledEvent;
  /** Frame aspect ratio (width / height). Defaults to 3:2 for the legacy page. */
  ratio?: number;
}): React.ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { start_time } = scheduledEvent;
  const isLive = scheduledEvent.status === Phase.Live.toString();

  /**
   * Current player status should be shown only when video is played.
   * Otherwise, the player status along with play button will be shown.
   */
  const [playRequested, setPlayRequested] = useState(false);

  const onPlay = useCallback(() => {
    setPlayRequested(true);
  }, []);

  return (
    <AspectRatio ratio={ratio}>
      <StyledDiv>
        <FallbackBackground
          className='background'
          backgroundImage={
            scheduledEvent.background_url ??
            scheduledEvent.webplayer_placeholder_image ??
            '/assets/fallback-background.jpeg'
          }
        />

        {playRequested &&
          (!isLive &&
          Time.now().timestamp >
            DateTime.fromISO(scheduledEvent.start_time).plus({ minutes: 15 }) ? (
            <States.Complete className='full' />
          ) : (
            <States.Countdown className='full' scheduledTime={DateTime.fromISO(start_time)} />
          ))}

        <Video
          streamUrl={scheduledEvent.assets?.stream_url}
          isLive={isLive}
          className='full z-3'
          onInitialPlay={onPlay}
          onPlay={onPlay}
        />
      </StyledDiv>
    </AspectRatio>
  );
};
